import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ImportHistoryModel, DataImportEntityEnum, DataImportResponseModel } from './models';
import { ResponseModel } from '../shared/models';
import { FileUploadParams } from 'common/src/models/file-upload-params.model';
import { selectCompanyProfile } from '../administration/store/selectors';
import { CompanyProfile } from '../administration/models/company-profile.model';
import { AppState } from '../store/state/app.state';
import { AdministrationsApiService } from '../administration/services/administrations-api.service';
import { environment } from '../../environments/environment';

@Injectable()
export class DataImportApiService {

  public companyProfile: CompanyProfile;

  public syncing$: BehaviorSubject<{syncing: boolean}> = new BehaviorSubject({syncing: false});
  public syncedAt$: BehaviorSubject<Date> = new BehaviorSubject(new Date());

  private readonly apiEndpoint: string = '/import';

  constructor(
    private http: HttpClient,
    private readonly store: Store<AppState>,
    private administrationsApiService: AdministrationsApiService,
  ) {
    this.store.select(selectCompanyProfile)
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
      });
  }

  private apiUrl(url: string = ''): string {
    return this.apiEndpoint + url;
  }

  public getSyncing$(): Observable<{ syncing: boolean }> {
    return this.syncing$ as Observable<{ syncing: boolean }>;
  }

  public setSyncing(syncing: boolean): void {
    this.syncing$.next({syncing});
  }

  public getSyncedAt$(): Observable<Date> {
    return this.syncedAt$ as Observable<Date>;
  }

  public setSyncedAt(): void {
    this.syncedAt$.next(new Date());
  }


  public getImportHistory(): Observable<ImportHistoryModel[]> {
    return this.http.get<ResponseModel<ImportHistoryModel[]>>(this.apiUrl('/history'))
      .pipe(
        map((response: ResponseModel<ImportHistoryModel[]>) => response.data)
      );
  }

  public getImportHistoryItem(id: number): Observable<Partial<DataImportResponseModel>> {
    return this.http.get<ResponseModel<Partial<DataImportResponseModel>>>(this.apiUrl(`/history/${id}`))
      .pipe(map((response: ResponseModel<Partial<DataImportResponseModel>>) => response.data));
  }

  public deleteImportHistoryItem(id: number): Observable<any> {
    return this.http.delete(this.apiUrl(`/history/${id}`));
  }

  public getImportTemplateParams(entityType: DataImportEntityEnum): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: this.apiUrl(`/${entityType}/template`),
      type: 'xlsx',
    };
    return of(fileParams);
  }

  public getImportPaymentsTemplateParams(): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: `${environment.javaApiVersion}/workspaces/${this.companyProfile.workspaceId}/payments/template`,
      type: 'xlsx',
    };
    return of(fileParams);
  }

  public getImportPartnersAccounts(): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: `${environment.javaApiVersion}/workspaces/${this.companyProfile.workspaceId}/reporting-period/partner-account/template`,
      type: 'xlsx',
    };
    return of(fileParams);
  }

  private getImportFormData(
    xlsxFile: File,
    strategy: 'validation'|'import',
    importId?: number,
    force = false,
    glProductTypeId?: number
  ): FormData {
    const formData = new FormData();

    formData.set('xlsx', xlsxFile);
    formData.set('strategy', strategy);
    formData.set('force', force as any);

    if (importId) {
      formData.set('importId', importId as any);
    }
    if (glProductTypeId) {
      formData.set('glProductTypeId', glProductTypeId as any);
    }

    return formData;
  }

  public importProducts(xlsxFile: File, strategy: 'validation'|'import', importId?: number, force = false, glProductTypeId?: number): Observable<DataImportResponseModel> {
    return this.http.post<ResponseModel<DataImportResponseModel>>(
      '/products/import',
      this.getImportFormData(xlsxFile, strategy, importId, force, glProductTypeId)
    )
      .pipe(
        tap(() => {
          if (!this.companyProfile.onboardingCompleted) {
            this.administrationsApiService.getOnboardingProcess().subscribe();
          }
        }),
        map((response: ResponseModel<DataImportResponseModel>) => response.data)
      );
  }

  public importProductsStock(xlsxFile: File, strategy: 'validation'|'import', importId?: number, force = false): Observable<DataImportResponseModel> {
    return this.http.post<ResponseModel<DataImportResponseModel>>(
      '/products/stock/import',
      this.getImportFormData(xlsxFile, strategy, importId, force)
    )
      .pipe(
        tap(() => {
          if (!this.companyProfile.onboardingCompleted) {
            this.administrationsApiService.getOnboardingProcess().subscribe();
          }
        }),
        map((response: ResponseModel<DataImportResponseModel>) => response.data)
      );
  }

  public importProductsPrices(xlsxFile: File, strategy: 'validation'|'import', salesPriceListName: string,  importId?: number, force = false): Observable<DataImportResponseModel> {
    const params = { salesPriceListName };
    return this.http.post<ResponseModel<DataImportResponseModel>>(
      '/products/prices/import',
      this.getImportFormData(xlsxFile, strategy, importId, force, null),
      { params }
    )
      .pipe(
        tap(() => {
          if (!this.companyProfile.onboardingCompleted) {
            this.administrationsApiService.getOnboardingProcess().subscribe();
          }
        }),
        map((response: ResponseModel<DataImportResponseModel>) => response.data)
      );
  }

  public importPartners(xlsxFile: File, strategy: 'validation'|'import', importId?: number, force = false): Observable<DataImportResponseModel> {
    return this.http.post<ResponseModel<DataImportResponseModel>>(
      '/partners/import',
      this.getImportFormData(xlsxFile, strategy, importId, force)
    )
      .pipe(
        tap(() => {
          if (!this.companyProfile.onboardingCompleted) {
            this.administrationsApiService.getOnboardingProcess().subscribe();
          }
        }),
        map((response: ResponseModel<DataImportResponseModel>) => response.data)
      );
  }

  public importPayments(xlsxFile: File, force = false): Observable<DataImportResponseModel> {
    const formData = new FormData();
    formData.set('file', xlsxFile);

    const params = {
      force: force as any
    };

    return this.http.post<ResponseModel<DataImportResponseModel>>(
      `${environment.javaApiVersion}/workspaces/${this.companyProfile.workspaceId}/payments/import`,
      formData,
      { params }
    )
      .pipe(
        tap(() => {
          if (!this.companyProfile.onboardingCompleted) {
            this.administrationsApiService.getOnboardingProcess().subscribe();
          }
        }),
        map((response: ResponseModel<DataImportResponseModel>) => response.data)
      );
  }

  public importPartnersAccounts(xlsxFile: File, force = false): Observable<DataImportResponseModel> {
    const formData = new FormData();
    formData.set('file', xlsxFile);

    const params = {
      force: force as any
    };

    return this.http.post<ResponseModel<DataImportResponseModel>>(
      `${environment.javaApiVersion}/workspaces/${this.companyProfile.workspaceId}/reporting-period/partner-account/import`,
      formData,
      { params }
    )
      .pipe(
        tap(() => {
          if (!this.companyProfile.onboardingCompleted) {
            this.administrationsApiService.getOnboardingProcess().subscribe();
          }
        }),
        map((response: ResponseModel<DataImportResponseModel>) => response.data)
      );
  }


}
